import { BrowserRouter as Router, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import Layout from "./components/layout/Layout";
import RoutesList from "./routes";
import i18n from "./i18n";
import { updateBackendOptions } from "./i18n";
import { trackPageView, updateConsent } from "./analytics";
import { useDispatch, useSelector } from "react-redux";

import { fetchCountryData } from "./store/countrySlice";
import { useCountry } from "./utils/countryUtils";
import { getInfo } from "./utils/urlUtil";
import { getStore, setStore } from "./utils/localStorageUtil";
import { PageMetaProvider } from "./context/PageMetaContext";
import { getHtmlLang } from "./constants/locales";
import ConsentModal from "./components/home/ConsentModal";

const LocationListener = ({ setCountryCode }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { getCountryDataByISO } = useCountry();

  useEffect(() => {
    if (location.pathname === "/thailand/th") { navigate("/th", { replace: true }); return; }
    if (location.pathname === "/russia") { navigate("/ru", { replace: true }); return; }
    if (location.pathname === "/uzbekistan") { navigate("/uz", { replace: true }); return; }
    if (location.pathname === "/uganda") { navigate("/ug", { replace: true }); return; }
    if (location.pathname === "/malaysia/en") { navigate("/my", { replace: true }); return; }
    if (location.pathname === "/ghana") { navigate("/gh", { replace: true }); return; }
    if (location.pathname === "/indonesia") { navigate("/id", { replace: true }); return; }
  }, [location.pathname, navigate]);

  const store = getStore();
  const storeCountryCode = store?.isoCountry;
  const storeEnv = store?.env;
  const storeLang = store?.lang || "eng";
  const urlInfo = getInfo();
  let countryCode = urlInfo.isoCountry || "gl";
  const env = urlInfo.env || "prod";
  const lang = "eng";

  const REDIRECT_ERROR_FLAG = "autoRedirectError";

  const setCountryClass = (code) => {
    document.body.classList.forEach((cls) => {
      if (cls.startsWith("country-")) document.body.classList.remove(cls);
    });
    document.body.classList.add(`country-${code.toLowerCase()}`);
  };

  const setDocumentAttributes = (country, code) => {
    const direction = country?.text_direction || "ltr";
    document.documentElement.setAttribute("dir", direction);
    document.body.setAttribute("dir", direction);

    // Language per market comes from src/constants/locales.js — the same table
    // that drives hreflang and the prerendered <head>, so they can never drift.
    document.documentElement.setAttribute("lang", getHtmlLang(code));
  };

  const getCountryAndRedirect = async (countries) => {
    try {
      const response = await fetch("https://api.country.is");
      const data = await response.json();
      const detectedCode = data.country.toLowerCase();
      const automaticCountry = getCountryDataByISO(detectedCode, countries);

      if (!automaticCountry || !automaticCountry.isInternalWebsite) {
        console.warn("Auto-redirect skipped: detected country is not an internal website", detectedCode);
        sessionStorage.setItem(REDIRECT_ERROR_FLAG, "true");
        if (window.location.pathname === "/") {
          navigate("/gl", { replace: true });
        }
        return;
      }

      if (!window.location.pathname.startsWith(`/${detectedCode}`)) {
        // Force a full reload so all country-scoped data is re-fetched for the new region.
        window.location.replace(`/${detectedCode}`);
      }
    } catch (error) {
      console.error("Error getting location:", error);
      sessionStorage.setItem(REDIRECT_ERROR_FLAG, "true");
      if (window.location.pathname === "/") {
        navigate("/gl", { replace: true });
      }
    }
  };

  useEffect(() => {
    trackPageView(location.pathname + location.search);

    if (storeCountryCode?.toLowerCase() == "undefined" || countryCode == "undefined") countryCode = "gl";

    if (storeCountryCode?.toLowerCase() != countryCode || storeEnv != env || storeLang != lang) {
      setStore({ isoCountry: countryCode, env, lang });
      updateBackendOptions({ newCountryId: countryCode, newEnv: env, lang: lang });
      i18n.changeLanguage(countryCode);
      setCountryClass(countryCode);

      const country = getCountryDataByISO(countryCode);
      if (country) setDocumentAttributes(country, countryCode);

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ site_region: countryCode.toUpperCase() });
    }
  }, [location]);

  useEffect(() => {
    dispatch(fetchCountryData(storeLang || lang)).then((resultAction) => {
      if (fetchCountryData.fulfilled.match(resultAction)) {
        const country = getCountryDataByISO(countryCode, resultAction.payload);
        const isValid = countryCode == "gl" || env == "preview" || (country && country.isInternalWebsite);

        if (!isValid && window.location.pathname !== "/") {
          navigate("/gl/pagenotfound");
          return null;
        }

        const hasRedirectError = sessionStorage.getItem("autoRedirectError") === "true";
        const hasAutoRedirected = sessionStorage.getItem("hasAutoRedirected") === "true";
        const path = window.location.pathname;
        // Geo-redirect only from the bare domain. /gl is the global edition and
        // the hreflang x-default target: bouncing it to a country site made
        // Googlebot see country content under the global URL, and denied users
        // any way to reach the global site on purpose.
        if (!hasRedirectError && !hasAutoRedirected && path === "/") {
          getCountryAndRedirect(resultAction.payload);
        }
        sessionStorage.setItem("hasAutoRedirected", "true");

        setCountryClass(countryCode);
        if (country) setDocumentAttributes(country, countryCode);
      } else if (fetchCountryData.rejected.match(resultAction)) {
        console.error("Error fetching countries:", resultAction.payload);
      }

      setCountryCode(countryCode);
    });
  }, []);

  return null;
};


const App = () => {
  const [countryCode, setCountryCode] = useState("gl");
  const consentAccepted = useSelector(state => state.general.consentAccepted);

  useEffect(() => {
    if (consentAccepted) {
      updateConsent(true);
    }
  }, [consentAccepted]);

  useEffect(() => {
    const handleCookiebotAccept = () => {
      if (window.Cookiebot) {
        // Cookiebot consent state available via window.Cookiebot.consent
      }
    };
    window.addEventListener("CookiebotOnAccept", handleCookiebotAccept);
    return () => window.removeEventListener("CookiebotOnAccept", handleCookiebotAccept);
  }, []);

  return (
    <Router>
      <LocationListener setCountryCode={setCountryCode} />
      <PageMetaProvider>
        <Layout>
          {(countryCode == "br" && !consentAccepted) && <ConsentModal />}
          <RoutesList />
        </Layout>
      </PageMetaProvider>
    </Router>
  );
};

export default App;
